import { useEffect, useRef, useState } from "react";
import { ArrowUp, ArrowDown, ArrowLeft, ArrowRight, RotateCcw, RotateCw } from "lucide-react";
import { useLoop, useSim, useWin, Slider, SimBtn, Readout } from "./simKit";

const KEYS = { w: "f", arrowup: "f", s: "b", arrowdown: "b", a: "l", d: "r", q: "yl", arrowleft: "yl", e: "yr", arrowright: "yr" };
const RINGS = [[200, 110], [560, 90], [680, 300], [380, 360], [140, 320]];
const GATES = [[240, 90], [520, 120], [700, 230], [560, 380], [260, 370], [110, 230]];

const PadBtn = ({ k, held, set, icon: Icon, label }) => (
  <button onPointerDown={(e) => { e.preventDefault(); set((h) => ({ ...h, [k]: true })); }} onPointerUp={() => set((h) => ({ ...h, [k]: false }))} onPointerLeave={() => set((h) => ({ ...h, [k]: false }))}
    className={`w-12 h-12 rounded-xl grid place-items-center transition-colors ${held[k] ? "bg-[#FF2E63] text-white" : "bg-white border border-slate-200 text-[#0A192F]"}`} style={{ touchAction: "none" }} aria-label={label} data-testid={`pad-${k}`}><Icon className="w-5 h-5" /></button>
);

export const FlightSim = ({ onSuccess, race = false }) => {
  const pts = race ? GATES : RINGS;
  const start = { x: 90, y: 400, vx: 0, vy: 0, h: -Math.PI / 2, idx: 0, t: 0, crash: 0, trail: [] };
  const [d, setD, dRef] = useSim(start);
  const [held, setHeld] = useState({});
  const [run, setRun] = useState(!race);
  const [best, setBest] = useState(null);
  const heldRef = useRef(held); heldRef.current = held;
  useEffect(() => {
    const on = (v) => (e) => { const k = KEYS[e.key.toLowerCase()]; if (k) { e.preventDefault(); setHeld((h) => ({ ...h, [k]: v })); } };
    const dn = on(true), up = on(false);
    window.addEventListener("keydown", dn); window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", dn); window.removeEventListener("keyup", up); };
  }, []);
  useLoop((dt) => {
    const p = dRef.current, k = heldRef.current;
    if (p.idx >= pts.length) return;
    const h = p.h + ((k.yr ? 1 : 0) - (k.yl ? 1 : 0)) * 2.6 * dt;
    const f = (k.f ? 1 : 0) - (k.b ? 1 : 0), s = (k.r ? 1 : 0) - (k.l ? 1 : 0);
    let vx = (p.vx + (Math.cos(h) * f - Math.sin(h) * s) * 260 * dt) * (1 - 1.6 * dt);
    let vy = (p.vy + (Math.sin(h) * f + Math.cos(h) * s) * 260 * dt) * (1 - 1.6 * dt);
    let x = p.x + vx * dt, y = p.y + vy * dt, crash = p.crash;
    if (x < 20 || x > 780 || y < 20 || y > 440) {
      if (race) { setD({ ...start, crash: crash + 1, t: p.t }); return; }
      x = Math.max(20, Math.min(780, x)); y = Math.max(20, Math.min(440, y)); vx = 0; vy = 0;
    }
    let idx = p.idx;
    if (Math.hypot(x - pts[idx][0], y - pts[idx][1]) < 34) idx += 1;
    const t = p.t + dt;
    if (idx >= pts.length) { setRun(false); if (race) setBest((b) => (b === null || t < b ? t : b)); }
    setD({ x, y, vx, vy, h, idx, t, crash, trail: [...p.trail.slice(-60), [x, y]] });
  }, run);
  const finished = d.idx >= pts.length;
  useWin(finished && (!race || d.t < 40), onSuccess);
  return (
    <div className="space-y-4">
      <svg viewBox="0 0 800 460" className="w-full rounded-2xl bg-[#0A192F] border border-[#0A192F]" data-testid="flight-stage">
        <defs><pattern id="fg" width="40" height="40" patternUnits="userSpaceOnUse"><path d="M40 0H0V40" fill="none" stroke="#16304F" /></pattern></defs>
        <rect width="800" height="460" fill="url(#fg)" /><rect x="10" y="10" width="780" height="440" fill="none" stroke={race ? "#FF2E63" : "#334155"} strokeWidth="3" rx="12" />
        {pts.map(([x, y], i) => (
          <g key={i} opacity={i < d.idx ? 0.25 : 1}>
            {race ? <rect x={x - 30} y={y - 30} width="60" height="60" rx="8" fill="none" stroke={i === d.idx ? "#FF2E63" : "#7C5CFF"} strokeWidth="6" />
              : <circle cx={x} cy={y} r="30" fill="none" stroke={i === d.idx ? "#FFD166" : "#4FD1C5"} strokeWidth="6" />}
            <text x={x} y={y + 6} textAnchor="middle" fill="#fff" fontSize="18" fontWeight="800">{String(i + 1)}</text>
          </g>))}
        <polyline points={d.trail.map((p) => p.join(",")).join(" ")} fill="none" stroke="#4FD1C5" strokeOpacity=".5" strokeWidth="3" />
        <g transform={`translate(${d.x} ${d.y}) rotate(${(d.h * 180) / Math.PI + 90})`}>
          <line x1="-16" y1="-16" x2="16" y2="16" stroke="#E2E8F0" strokeWidth="5" /><line x1="16" y1="-16" x2="-16" y2="16" stroke="#E2E8F0" strokeWidth="5" />
          {[[-16, -16], [16, -16], [-16, 16], [16, 16]].map(([a, b], i) => <circle key={i} cx={a} cy={b} r="9" fill={i < 2 ? "#FF2E63" : "#4FD1C5"} opacity=".8" />)}
          <rect x="-7" y="-9" width="14" height="18" rx="4" fill="#0EA5E9" />
        </g>
      </svg>
      <Readout items={[[race ? "Gate" : "Ring", `${Math.min(d.idx, pts.length)} / ${pts.length}`, finished ? "good" : ""], ["Time", `${d.t.toFixed(1)} s`, race && finished ? (d.t < 40 ? "good" : "bad") : ""],
        ...(race ? [["Crashes", d.crash, d.crash ? "bad" : ""], ["Best lap", best === null ? "-" : `${best.toFixed(1)} s`]] : []), ...(finished ? [["Result", race ? (d.t < 40 ? "Lap under 40 s!" : "Finished — go faster!") : "All rings cleared!", race && d.t >= 40 ? "bad" : "good"]] : [])]} />
      <div className="flex flex-wrap items-center gap-6">
        <div className="grid grid-cols-3 gap-1.5 w-fit"><span /><PadBtn k="f" held={held} set={setHeld} icon={ArrowUp} label="Pitch forward" /><span />
          <PadBtn k="l" held={held} set={setHeld} icon={ArrowLeft} label="Roll left" /><PadBtn k="b" held={held} set={setHeld} icon={ArrowDown} label="Pitch back" /><PadBtn k="r" held={held} set={setHeld} icon={ArrowRight} label="Roll right" /></div>
        <div className="flex gap-1.5"><PadBtn k="yl" held={held} set={setHeld} icon={RotateCcw} label="Yaw left" /><PadBtn k="yr" held={held} set={setHeld} icon={RotateCw} label="Yaw right" /></div>
        <div className="flex gap-2">
          {race && <SimBtn onClick={() => { setD(start); setRun(true); }} testid="flight-start-btn">{finished ? "Race again" : "Start"}</SimBtn>}
          <SimBtn tone="light" onClick={() => { setD(start); setRun(!race); }} testid="flight-reset-btn">Reset</SimBtn>
        </div>
      </div>
      <p className="text-xs text-slate-500">Keyboard: W/S pitch · A/D roll · Q/E or ←/→ yaw. The nose (red props) points forward.</p>
    </div>
  );
};

export const RaceSim = (props) => <FlightSim {...props} race />;

export const GimbalSim = ({ onSuccess }) => {
  const [P, setP] = useState(0.2);
  const [D, setDg] = useState(0.0);
  const [wind, setWind] = useState(true);
  const [s, setS, sRef] = useSim({ t: 0, body: 0, cam: 0, camV: 0, err: [], score: 0 });
  useLoop((dt) => {
    const p = sRef.current, t = p.t + dt;
    const body = wind ? 18 * Math.sin(t * 2.3) + 9 * Math.sin(t * 5.7 + 1) : 0;
    const horizon = p.cam + body;
    const acc = -(P * 100) * horizon - (D * 10) * p.camV;
    const camV = p.camV + acc * dt, cam = p.cam + camV * dt;
    const err = [...p.err.slice(-120), Math.abs(cam + body)];
    const avg = err.reduce((a, b) => a + b, 0) / err.length;
    const score = Math.max(0, Math.min(100, 100 - avg * 5));
    setS({ t, body, cam, camV, err, score });
  }, true);
  const tilt = s.cam + s.body;
  useWin(wind && s.t > 3 && s.score >= 90, onSuccess);
  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-2 gap-3">
        <svg viewBox="0 0 400 260" className="w-full rounded-2xl bg-gradient-to-b from-[#BFE3FF] to-[#E8F0FE] border border-slate-200" data-testid="gimbal-drone">
          <g transform={`translate(200 120) rotate(${s.body})`}>
            <rect x="-110" y="-8" width="220" height="16" rx="8" fill="#0A192F" />{[-110, 110].map((x) => <ellipse key={x} cx={x} cy="-16" rx="44" ry="6" fill="#FF2E63" opacity=".7" />)}
            <g transform={`rotate(${s.cam})`}><rect x="-6" y="8" width="12" height="30" fill="#64748B" /><rect x="-26" y="36" width="52" height="34" rx="8" fill="#334155" /><circle cx="0" cy="53" r="11" fill="#0EA5E9" /></g>
          </g>
          <text x="12" y="248" fontSize="14" fill="#0A192F">{`Drone tilt ${s.body.toFixed(0)}°`}</text>
        </svg>
        <svg viewBox="0 0 400 260" className="w-full rounded-2xl bg-[#0A192F] border border-[#0A192F]" data-testid="gimbal-camera-view">
          <g transform={`rotate(${-tilt} 200 130)`}><rect x="-200" y="130" width="800" height="400" fill="#3B7A57" /><rect x="-200" y="-270" width="800" height="400" fill="#6EB6FF" /><circle cx="290" cy="70" r="22" fill="#FFD166" /></g>
          <rect x="4" y="4" width="392" height="252" rx="10" fill="none" stroke="#fff" strokeOpacity=".4" strokeWidth="2" />
          <text x="12" y="246" fontSize="14" fill="#fff">{`Camera view · horizon ${tilt.toFixed(1)}°`}</text>
        </svg>
      </div>
      <Readout items={[["Stability score", `${s.score.toFixed(0)}%`, s.score >= 90 ? "good" : "bad"], ["Wind", wind ? "ON" : "OFF"]]} />
      <div className="grid sm:grid-cols-2 gap-4">
        <Slider label="P gain (correction)" value={P} min={0} max={4} step={0.05} onChange={setP} testid="sim-p-gain" />
        <Slider label="D gain (damping)" value={D} min={0} max={3} step={0.05} onChange={setDg} testid="sim-d-gain" />
      </div>
      <SimBtn tone={wind ? "pink" : "light"} onClick={() => setWind(!wind)} testid="sim-wind-toggle">Wind: {wind ? "ON" : "OFF"}</SimBtn>
    </div>
  );
};
